import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { BuildReviewPanel } from "@/components/BuildReviewPanel";
import { PaginationBar } from "@/components/PaginationBar";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider } from "@/components/cockpit/CockpitShell";
import { getStageConfig } from "@/data/workflowEngine";
import { deriveBuildReviewStatus } from "@/data/buildReviewTypes";
import { INTAKE_REVIEW_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { useIntakeReviewServerData } from "@/hooks/useIntakeReviewServerPaging";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import type { IntakeReviewAgeBucket } from "@/services/intake-review-page.service";

type IntakeReviewBucketFilter = IntakeReviewAgeBucket | "all";

export default function IntakeReview() {
  const appData = useAppData();
  const [activeBucket, setActiveBucket] = useState<IntakeReviewBucketFilter>("all");
  const serverPagination = useCountedServerPagination(20);
  const { reset: resetServerPagination, setTotalItems: setServerTotalItems } = serverPagination;
  const handleBucketChange = useCallback((bucket: IntakeReviewBucketFilter) => {
    resetServerPagination();
    setActiveBucket(bucket);
  }, [resetServerPagination]);

  const serverData = useIntakeReviewServerData({
    enabled: INTAKE_REVIEW_SERVER_PAGING_ENABLED,
    page: serverPagination.page,
    pageSize: serverPagination.pageSize,
    bucket: activeBucket,
  });

  useEffect(() => {
    if (INTAKE_REVIEW_SERVER_PAGING_ENABLED) {
      setServerTotalItems(serverData.count);
    }
  }, [serverData.count, setServerTotalItems]);

  const jobs = INTAKE_REVIEW_SERVER_PAGING_ENABLED ? serverData.jobs : appData.jobs;
  const issues = INTAKE_REVIEW_SERVER_PAGING_ENABLED ? serverData.issues : appData.issues;
  const buildReviewEvents = INTAKE_REVIEW_SERVER_PAGING_ENABLED
    ? serverData.buildReviewEvents
    : appData.buildReviewEvents;
  const setJobs = INTAKE_REVIEW_SERVER_PAGING_ENABLED ? serverData.setJobs : appData.setJobs;

  const derivedCounts = useMemo(() => {
    const open = jobs.filter(j => (getStageConfig(j.currentStage)?.order ?? 0) < 100);
    let pending = 0, onHold = 0, awaitingSig = 0;
    open.forEach(job => {
      const evts = buildReviewEvents.filter(e => e.jobId === job.id);
      const status = deriveBuildReviewStatus(evts);
      if (status === "Approved") return;
      pending++;
      if (status === "On Hold — Sales Action Required" || status === "Revision Required") onHold++;
      if (status === "Awaiting Customer Signature") awaitingSig++;
    });
    return { pending, onHold, awaitingSig };
  }, [jobs, buildReviewEvents]);

  const counts = INTAKE_REVIEW_SERVER_PAGING_ENABLED && serverData.summary
    ? {
        pending: serverData.summary.pending,
        onHold: serverData.summary.onHold,
        awaitingSig: serverData.summary.awaitingSignature,
      }
    : derivedCounts;

  const serverBucketCounts = serverData.summary
    ? {
        overdue: serverData.summary.overdue,
        "at-risk": serverData.summary.atRisk,
        watch: serverData.summary.watch,
        fresh: serverData.summary.fresh,
      }
    : undefined;

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Build Review"
        right={
          <div className="flex items-center gap-4">
            <HeroMetric label="Pending" value={counts.pending} />
            <HeroDivider />
            <HeroMetric label="On Hold" value={counts.onHold} tone={counts.onHold > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Awaiting Sig" value={counts.awaitingSig} />
            <HeroDivider />
            <ReportFrictionButton moduleName="Build Review" />
          </div>
        }
      />

      {INTAKE_REVIEW_SERVER_PAGING_ENABLED ? (
        <BuildReviewPanel
          jobs={jobs}
          issues={issues}
          setJobs={setJobs}
          buildReviewEvents={buildReviewEvents}
          totalCount={serverData.count}
          bucketCounts={serverBucketCounts}
          activeBucket={activeBucket}
          onBucketChange={handleBucketChange}
          isLoading={serverData.loading}
          isError={!!serverData.error}
          onRefresh={serverData.refresh}
        />
      ) : (
        <BuildReviewPanel />
      )}
      {INTAKE_REVIEW_SERVER_PAGING_ENABLED && <PaginationBar {...serverPagination} />}
    </CockpitPage>
  );
}
